<script setup lang="ts">
import {computed, onUnmounted, ref} from "vue";
import {ClipboardCheck} from "@lucide/vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {useTreatmentSelectionsStore} from "@/medication-checkup/stores/treatment-selections-store";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupScanCard from "@/medication-checkup/components/MedicationCheckupScanCard.vue";
import MedicationCheckupTreatmentSelectionCard
    from "@/medication-checkup/components/MedicationCheckupTreatmentSelectionCard.vue";
import MedicationCheckupRetainedTreatmentsList
    from "@/medication-checkup/components/MedicationCheckupRetainedTreatmentsList.vue";
import MedicationCheckupRetainedTreatmentsDialog
    from "@/medication-checkup/components/MedicationCheckupRetainedTreatmentsDialog.vue";
import MedicationCheckupPrescriptionViewer
    from "@/medication-checkup/components/MedicationCheckupPrescriptionViewer.vue";
import MedicationCheckupDosageDialog from "@/medication-checkup/components/MedicationCheckupDosageDialog.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {TreatmentLine, TreatmentSelectionsView} from "@/medication-checkup/interfaces/treatment-selections";
import {TYPING_MS, WORK_FLOOR_MS} from "@/medication-checkup/constants";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useScanRun} from "@/medication-checkup/composables/useScanRun";
import LinePosologies from "@/medication-checkup/services/LinePosologies";
import TreatmentSheets, {TreatmentSheet} from "@/medication-checkup/services/TreatmentSheets";
import PrescriptionPages from "@/medication-checkup/services/PrescriptionPages";
import {CARD_SHEET} from "@/medication-checkup/services/SceneClasses";

/**
 * The scene of the treatment selections block.
 */

const props = defineProps<SceneProps<TreatmentSelectionsView>>();

const store = useMedicationCheckupStore();

/**
 * The review in progress — checks and posologies, local until the closing
 * action submits them. It lives in its own store so the checklist of the
 * step follows it, and starts over with every live visit, since coming back
 * re-runs the scan, which seeds it again.
 */
const selection = useTreatmentSelectionsStore();

/**
 * A frozen scene renders the collection whole; a live one re-runs the scan on
 * every visit, so the collection tracks the record while it is open.
 */
const {bubbleShown, clicked, view, text, actions, acted, later, act: submit} = useScene(props, {
    frozen: () => revealStep.value = 3,
    live: () => {
        selection.start(props.block.id);
        collect();
    }
});

/**
 * The prescription pages fetched for the scene are let go with it.
 */
onUnmounted(() => {
    selection.stop(props.block.id);
    PrescriptionPages.release();
});

/**
 * The scan of the record, run behind the card and revealed step by step. Its
 * minimum display is armed as the scene opens — the typing of the bubble
 * included — so the first card of the course does not flash past.
 */
const {
    run,
    done: scanDone,
    failed: collectFailed,
    progress: scanProgress,
    revealStep,
    start: collect
} = useScanRun({
    bubbleShown,
    later,
    stream: (report, signal) => store.scanBlock(props.block.id, report, signal),
    fallback: () => store.submitBlock(props.block.id, {action: 'collect'}),
    landed: () => preselect(),
    floor: TYPING_MS + WORK_FLOOR_MS
});

const adding = ref(false);

const subject = computed(() => view.value?.subject ?? null);

const found = computed(() => view.value?.found ?? null);

const reminder = computed(() => view.value?.reminder ?? null);

const lines = computed(() => view.value?.lines ?? []);

/**
 * Seed the selection with the lines the HDS suggests, on top of whatever is
 * already checked — a suggestion is a starting point, never a lock.
 */
const preselect = () => selection.check(lines.value.filter(line => line.suggested).map(line => line.id));

/**
 * Add a product from the referential, with its source. The line arrives
 * checked — it was just named with the patient, so adding it is the review —
 * and its posology opens at once.
 */
const add = async (productId: number, source: TreatmentLine['source']) => {
    if (adding.value) {
        return;
    }

    adding.value = true;
    const before = lines.value.map(line => line.id);

    try {
        const submitted = await store.submitBlock(props.block.id, {
            action: 'add_line',
            product_id: productId,
            source
        });

        // A refused submission still refreshes the served state, so the new
        // lines it brings back are someone else's work — checking them would
        // retain treatments the pharmacist never added.
        if (!submitted) {
            return;
        }

        const added = lines.value.filter(line => !before.includes(line.id));

        selection.check(added.map(line => line.id));

        if (added.length === 1) {
            selection.edit(added[0]);
        }
    } finally {
        adding.value = false;
    }
};

/**
 * Delete a hand-added line. It leaves the section at once and comes back if
 * the server refuses — the store has then said why.
 */
const remove = async (line: TreatmentLine) => {
    if (selection.removing.includes(line.id)) {
        return;
    }

    selection.startRemoving(line);

    try {
        const submitted = await store.submitBlock(props.block.id, {
            action: 'remove_line',
            line_id: line.id
        });

        if (submitted) {
            selection.forget(line);
        }
    } finally {
        selection.stopRemoving(line);
    }
};

/**
 * The prescriptions open full screen, and the one shown first.
 */
const viewing = ref<{sheets: TreatmentSheet[]; start: number} | null>(null);

const openSheets = (sheets: TreatmentSheet[], start = 0) => {
    viewing.value = {sheets, start};
};

/**
 * The scanned prescriptions of the line whose posology is edited, shown
 * beside the form.
 */
const editingSheets = computed(() => selection.editing ? TreatmentSheets.sheetsOf(selection.editing) : []);

/**
 * The closing action waiting on the last look at the retained treatments.
 */
const closing = ref<MedicationCheckupAction | null>(null);

/**
 * Close the collection on the selection with its posologies — the ones the
 * pharmacist saved, and the proposals read and shown.
 */
const validate = () => {
    const action = closing.value;

    closing.value = null;

    if (action) {
        submit(action, selection.payload());
    }
};

/**
 * The record left behind once validated: the lines confirmed, with the
 * posologies they were written with.
 */
const recorded = computed(() => lines.value
    .filter(line => line.confirmed)
    .map(line => ({line, posology: LinePosologies.recorded(line)})));
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupCopiloteLine v-if="found" :text="found" />
      <div
        v-if="recorded.length > 0"
        :class="CARD_SHEET"
        class="max-w-[720px] overflow-hidden rounded-xl"
      >
        <div class="flex items-center gap-2 border-b border-solid border-gray-light px-3 py-2.5">
          <ClipboardCheck class="size-[18px] text-primary" />
          <span class="flex-1 text-sm font-semibold text-primary">Traitements retenus</span>
          <span class="text-sm font-semibold text-secondary-darkest">{{ recorded.length }}</span>
        </div>
        <MedicationCheckupRetainedTreatmentsList
          :entries="recorded"
          @view="openSheets"
        />
      </div>
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupScanCard
        v-if="bubbleShown"
        :failed="collectFailed"
        :done="scanDone"
        :progress="scanProgress"
        :run="run"
        :subject="subject"
        class="animate-message-in"
        @retry="collect"
      />

      <MedicationCheckupCopiloteLine
        v-if="revealStep >= 1 && found"
        :text="found"
        class="animate-message-in"
      />

      <MedicationCheckupTreatmentSelectionCard
        v-if="revealStep >= 2"
        :lines="lines"
        :reminder="reminder"
        :adding="adding"
        :removing="selection.removing"
        class="animate-message-in"
        @edit="selection.edit"
        @open="openSheets"
        @add="add"
        @remove="remove"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="revealStep >= 3"
        @act="closing = $event"
      />

      <MedicationCheckupRetainedTreatmentsDialog
        :open="closing !== null"
        @close="closing = null"
        @validate="validate"
        @edit="selection.edit"
      />

      <MedicationCheckupDosageDialog
        :line="selection.editing"
        :draft="selection.editingDraft"
        :sheets="editingSheets"
        @close="selection.stopEditing"
        @save="selection.keep"
      />
    </template>

    <MedicationCheckupPrescriptionViewer
      v-if="viewing"
      :sheets="viewing.sheets"
      :start="viewing.start"
      @close="viewing = null"
    />
  </div>
</template>
